import { useEffect } from 'react';
import { useLocalObservable } from 'mobx-react';
import XRegExp from 'xregexp';
import pubSub from '../../../../../utils/pubsub';

/**
 * Highlights the search key within the given name text
 * @param {string} name - The text to search within
 * @param {string} searchKey - The search key to highlight
 * @param {function} createElement - React.createElement or similar
 * @returns {Array} Array of text and highlighted span elements
 */
export const highlightSearchKey = (name, searchKey, createElement) => {
  if (!searchKey) return name;

  const normalizedSearchKey = XRegExp.escape(
    searchKey
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
  );

  const regex = XRegExp(`(${normalizedSearchKey})`, 'gi');
  const normalizedName = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

  const parts = XRegExp.split(normalizedName, regex);

  let currentIndex = 0;
  return parts.map((part, index) => {
    const originalPart = name.slice(currentIndex, currentIndex + part.length);
    currentIndex += part.length;

    if (XRegExp.test(part, regex)) {
      return createElement(
        'span',
        { key: index, className: 'highlight-option-name' },
        originalPart
      );
    }
    return originalPart;
  });
};

/**
 * Hook for managing context menu state with pubsub integration
 * @returns {Object} State object with toggledLinkContextMenu, toggleLinkContextMenu, closeLinkContextMenu
 */
export const useContextMenuState = () => {
  const state = useLocalObservable(() => ({
    toggledLinkContextMenu: false,
    toggleLinkContextMenu: () => {
      state.toggledLinkContextMenu = !state.toggledLinkContextMenu;
    },
    closeLinkContextMenu: () => {
      state.toggledLinkContextMenu = false;
    }
  }));

  useEffect(() => {
    const onAnyClick = () => {
      state.closeLinkContextMenu();
    };

    const onPubSubContextClick = (payload) => {
      if (payload?.event === 'icon-button-context-menu-trigger') {
        state.closeLinkContextMenu();
      }
    };

    pubSub.subscribe(onPubSubContextClick);
    document.addEventListener('click', onAnyClick);

    return () => {
      pubSub.unsubscribe(onPubSubContextClick);
      document.removeEventListener('click', onAnyClick);
    };
  }, [state]);

  return state;
};

/**
 * Updates the URL search query parameter without adding to browser history
 * @param {string} query - The search query to set (empty to remove param)
 */
export const updateUrlSearchQuery = (query) => {
  const params = new URLSearchParams(window.location.search);
  if (query && query.trim()) {
    params.set('q', query.trim());
  } else {
    params.delete('q');
  }
  const newUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState({}, '', newUrl);
};

/**
 * Creates a keyboard handler for navigating search results
 * @param {function} handleClick - Click handler to call on Enter
 * @param {string} inputId - ID of the search input to focus
 * @returns {function} Keyboard event handler
 */
export const createKeyboardNavigationHandler = (handleClick, inputId) => {
  return (event) => {
    const isEnter = event.key === 'Enter';
    const isDown = event.key === 'ArrowDown';
    const isUp = event.key === 'ArrowUp';

    if (isEnter) {
      handleClick();
      return;
    }

    if (!isDown && !isUp) return;

    const entries = document.querySelectorAll('.profiler-name-search-result-entry');
    const count = entries.length;
    if (count === 0) return;

    const currentIndex = Array.from(entries).indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let target = null;

    if (isDown) {
      if (currentIndex === count - 1) {
        target = document.getElementById(inputId);
      } else {
        target = entries[currentIndex + 1];
      }
    } else if (isUp) {
      if (currentIndex === 0) {
        target = document.getElementById(inputId);
      } else {
        target = entries[currentIndex - 1];
      }
    }

    if (target) {
      event.preventDefault();
      event.stopPropagation();
      target.focus();
    }
  };
};
